import { z } from "zod";
import type { AIServiceDateParams } from "../ai-services/types";

// ── API params ──

export type ConversationsParams = AIServiceDateParams & {
  page?: number;
  page_size?: number;
  sort?: string;
};

export type ConversationTraceParams = AIServiceDateParams;

export interface TraceQueryParams {
  request_id: string;
}

// ── Response schemas ──

/**
 * A span kind we do not know yet.
 *
 * A strict enum would fail the whole trace the day the backend emits a new
 * kind, and `.catch`ing to one of the known kinds would be worse — mapping an
 * unknown span onto `request_event` would silently drop it from the waterfall,
 * which filters those out. It gets its own name, its own neutral colour, and
 * stays visible.
 */
export const UNKNOWN_SPAN_KIND = "unknown";

export const spanKindSchema = z
  .enum(["llm_step", "tool_call", "request_event", UNKNOWN_SPAN_KIND])
  .catch(UNKNOWN_SPAN_KIND);
export type SpanKind = z.infer<typeof spanKindSchema>;

export const spanStatusSchema = z.enum(["ok", "error"]);
export type SpanStatus = z.infer<typeof spanStatusSchema>;

export const conversationSchema = z.object({
  conversation_id: z.string(),
  // One request id from this conversation. Drives "run it yourself" — the only
  // trace affordance available when trace_available is false.
  sample_request_id: z.string().nullish(),
  user_name: z.string(),
  client: z.string().nullable().default(null),
  started_at: z.string(),
  ended_at: z.string(),
  duration_ms: z.number(),
  agent_ms: z.number(),
  turns: z.number(),
  steps: z.number(),
  tool_calls: z.number(),
  tools_used: z.array(z.string()).default([]),
  status: spanStatusSchema,
  credits: z.number(),
  cost: z.number(),
  cache_read_tokens: z.number(),
  cache_write_tokens: z.number(),
  cached_pct: z.number(),
  peak_context_tokens: z.number(),
  trace_available: z.boolean(),
  // Warehouse spend for the WHOLE conversation. A query issued between two
  // turns belongs to no turn, so summing `turn.query_cost` reports less than
  // the customer was billed — this is the figure the summary card shows.
  query_cost: z.number().default(0),
  query_count: z.number().default(0),
});
export type Conversation = z.infer<typeof conversationSchema>;

// The backend uses the shared `PaginationInfo` envelope (`page`/`pages`/`size`/
// `total`) that the rest of the API returns — not flat page fields.
export const paginationSchema = z.object({
  page: z.number(),
  pages: z.number(),
  size: z.number(),
  total: z.number(),
});

export const conversationsResponseSchema = z.object({
  conversations: z.array(conversationSchema),
  pagination: paginationSchema,
});
export type ConversationsResponse = z.infer<typeof conversationsResponseSchema>;

// Span-level token counts use short keys (`input`); turn-level counts use the
// long form (`input_tokens`). The backend contract differs between the two —
// do not "harmonise" them here or the numbers silently become undefined.
export const spanTokensSchema = z.object({
  input: z.number(),
  output: z.number(),
  cache_read: z.number(),
  cache_write: z.number(),
});
export type SpanTokens = z.infer<typeof spanTokensSchema>;

export const spanSchema = z.object({
  // Instantaneous `request_event` rows are events, not spans, so Snowflake
  // gives them no span id. They are filtered out of the waterfall.
  span_id: z.string().nullable().default(null),
  name: z.string(),
  kind: spanKindSchema,
  start_offset_ms: z.number(),
  duration_ms: z.number(),
  status: spanStatusSchema,
  status_detail: z.string().nullable().default(null),
  model: z.string().nullable().default(null),
  step_number: z.number().nullable().default(null),
  tokens: spanTokensSchema.nullable().default(null),
  // The backend resolves the tool name from the span name; use it rather than
  // re-parsing "ToolCall-<tool>" on the client.
  tool: z.object({ name: z.string() }).nullable().default(null),
  // What an LLM step invoked, with the arguments passed — the SQL and shell
  // commands the agent ran. Tool RESULTS are deliberately not collected.
  //
  // Argument VALUES are `unknown`, not strings: a `limit` is a number, a flag
  // is a boolean, a filter is a nested object. Requiring strings failed the
  // parse on ordinary traces, and `applyCatchToSchema` cannot recurse into a
  // ZodRecord to rescue it — so the whole response came back raw and every
  // default below was silently dropped. Renderers stringify (`toolCallText`).
  tool_calls: z
    .array(z.object({ name: z.string(), arguments: z.record(z.unknown()).default({}) }))
    .default([]),
});
export type Span = z.infer<typeof spanSchema>;

export const turnTokensSchema = z.object({
  input_tokens: z.number(),
  output_tokens: z.number(),
  cache_read_tokens: z.number(),
  cache_write_tokens: z.number(),
});
export type TurnTokens = z.infer<typeof turnTokensSchema>;

export const turnSchema = z.object({
  turn_number: z.number(),
  message_id: z.string().nullable().default(null),
  request_id: z.string().nullable().default(null),
  start_offset_ms: z.number(),
  duration_ms: z.number(),
  credits: z.number(),
  cost: z.number(),
  // Nullable for the same reason `spanSchema.tokens` is: a turn that failed
  // before it reached a model spent no tokens, and Snowflake reports no split
  // for it. Requiring the object failed the parse and dropped the whole trace.
  tokens: turnTokensSchema.nullable().default(null),
  spans: z.array(spanSchema).default([]),
  // What the SQL this turn issued cost the warehouse. Zero when the turn ran no
  // queries, or when the conversation predates session-id capture.
  query_cost: z.number().default(0),
  query_count: z.number().default(0),
});
export type Turn = z.infer<typeof turnSchema>;

export const insightSeveritySchema = z.enum(["critical", "high", "medium", "low"]);
export type InsightSeverity = z.infer<typeof insightSeveritySchema>;

// Findings arrive ranked by `score` (severity x impact x confidence). Below 0.5
// confidence the backend is guessing, and the panel collapses those out of the
// way rather than letting them dilute the ones it is sure about.
export const insightSchema = z.object({
  kind: z.string(),
  headline: z.string(),
  severity: insightSeveritySchema.catch("low"),
  detail: z.string().default(""),
  impact_label: z.string().nullable().default(null),
  confidence: z.number().default(1),
  score: z.number().default(0),
  // Where to look. Drives the "show me" jump into the waterfall.
  evidence_span_id: z.string().nullable().default(null),
  evidence_turn: z.number().nullable().default(null),
  // Every turn the finding touches, so it can offer a jump per turn.
  evidence_turns: z.array(z.number()).default([]),
  // A paste-ready policy line, for findings whose fix is a rule not an edit.
  guardrail: z.string().nullable().default(null),
});
export type Insight = z.infer<typeof insightSchema>;

export const MINOR_INSIGHT_CONFIDENCE = 0.5;

/** Beyond this the list stops being a ranking and becomes a wall. */
export const MAX_VISIBLE_INSIGHTS = 4;

export const traceResponseSchema = z.object({
  conversation: conversationSchema,
  turns: z.array(turnSchema).default([]),
  insights: z.array(insightSchema).default([]),
});
export type TraceResponse = z.infer<typeof traceResponseSchema>;

export const traceQueryResponseSchema = z.object({
  sql: z.string(),
  snowsight_url: z.string().nullable().default(null),
  note: z.string().nullable().default(null),
});
export type TraceQueryResponse = z.infer<typeof traceQueryResponseSchema>;

// ── Waterfall view model ──

/** One rendered bar. `kind` drives colour; `status` only adds the error marker. */
export interface WaterfallRow {
  key: string;
  label: string;
  indent: number;
  offsetMs: number;
  durationMs: number;
  kind: SpanKind | "turn";
  status: SpanStatus;
  turnNumber: number;
  isTurn: boolean;
  detail: string;
  /** Why a failed span failed — the tool's own error text, already truncated. */
  statusDetail: string | null;
}
